import {FC, PropsWithChildren, useCallback, useEffect, useState} from "react";
import {useLazyQuery} from "@apollo/client";
import {
  LookupUserAuthorizations,
  LookupUserAuthorizationsData,
  LookupUserAuthorizationsVariables
} from "../backend";
import {UserAuthorizationContext} from "./UserAuthorizationContext";
import {Endpoints} from "../../runtime";
import {operationalHierarchyPermissions} from "../OperationalHierarchyPermissions";
import {transformUserAuthorizations} from "./UserAuthorizationsMap";
import {Permission} from "../Permission";
import {OperationalHierarchyPermission} from "../OperationalHierarchyPermission";
import {useOktaAuth} from "@okta/okta-react";
import {useFlags} from "launchdarkly-react-client-sdk";
import {features} from "../../features";
import {useAmplifyClient} from "../../amplify";
import {
  userAuthorizations as userAuthorizationsQuery,
  LookupUserAuthorizationsQuery,
  LookupUserAuthorizationsQueryVariables
} from "../../amplify/graphql";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";

// For now there is a mismatch between the permissions type in the UI and the authorization type on the backend
// To keep things clean we will transform the permissions to the backend type here
const operationalHierarchyPermissionsTransform = (
  operationalHierarchyPermissions: OperationalHierarchyPermission[]
) => {
  return operationalHierarchyPermissions.map(permission => ({
    resourceIdentifier: permission.instance,
    resourceType: permission.resource,
    action: permission.action
  }));
};

export interface UserAuthorizationProviderProps {
  waitForLoading?: boolean;
}

export const UserAuthorizationProvider: FC<
  PropsWithChildren<UserAuthorizationProviderProps>
> = ({waitForLoading = true, children}) => {
  const {authState} = useOktaAuth();
  const flags = useFlags();
  const useAmplify =
    flags[features.dev.amplifyClient.useAmplifyForUserAuthorizations];

  // === Apollo path (existing) ===
  const [lookupUserAuthorizations, {data: apolloData, error: apolloError}] =
    useLazyQuery<
      LookupUserAuthorizationsData,
      LookupUserAuthorizationsVariables
    >(LookupUserAuthorizations, {
      variables: {
        userAuthorizations: operationalHierarchyPermissionsTransform(
          operationalHierarchyPermissions
        )
      },
      context: {
        endpoint: Endpoints.APPSYNC
      }
    });

  // === Amplify path (new) ===
  const amplifyClient = useAmplifyClient();
  const [amplifyData, setAmplifyData] =
    useState<LookupUserAuthorizationsData | null>(null);
  const [amplifyError, setAmplifyError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (authState?.isAuthenticated && useAmplify && amplifyClient) {
      console.log("Using Amplify Client for Authorization Lookups");
      amplifyClient
        .graphql<GraphQLQuery<LookupUserAuthorizationsQuery>>({
          query: userAuthorizationsQuery,
          variables: {
            userAuthorizations: operationalHierarchyPermissionsTransform(
              operationalHierarchyPermissions
            )
          } as LookupUserAuthorizationsQueryVariables
        })
        .then(response => {
          if (cancelled) return;
          setAmplifyData({
            userAuthorizations:
              (response.data
                .userAuthorizations as LookupUserAuthorizationsData["userAuthorizations"]) ??
              []
          });
        })
        .catch(err => {
          if (cancelled) return;
          setAmplifyError(err instanceof Error ? err : new Error(String(err)));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [authState?.isAuthenticated, useAmplify, amplifyClient]);

  useEffect(() => {
    if (authState?.isAuthenticated && !useAmplify) {
      lookupUserAuthorizations();
    }
  }, [authState, useAmplify, lookupUserAuthorizations]);

  // Select active data/error based on feature flag
  const data = useAmplify ? amplifyData : apolloData;
  const error = useAmplify ? amplifyError : apolloError;

  const isUserAuthorized = useCallback(
    (permissions: Permission[]) => {
      if (error || !data) {
        return false;
      }

      const userAuthorizationsMap = transformUserAuthorizations(
        data.userAuthorizations ?? []
      );

      return permissions.every(permission => {
        const key = `${permission.instance}.${permission.resource}.${permission.action}`;
        return userAuthorizationsMap[key];
      });
    },
    [data, error]
  );

  // Returns the children when:
  // 1. When the user is authenticated and the query returned data or an error. This is the happy path case.
  // 2. When the user is not authenticated (therefore no data and no error since the query was not called)

  // Since this page is above the Routes, we don't want to break the rendering of the child routes (such as the login callback) when the user is not authenticated
  // But when the user is authenticated, we want to make sure that the query is called and completed before rendering the children

  return data || error || !authState?.isAuthenticated || !waitForLoading ? (
    <UserAuthorizationContext.Provider
      value={{
        isUserAuthorized
      }}>
      {children}
    </UserAuthorizationContext.Provider>
  ) : null;
};
